import { useCallback, useEffect, useRef, useState } from 'react';
import { CommentReturn } from '../../clients/rust-api-client';
import { useSyncedComments } from '../../sync/hooks';
import { billingErrorMessage } from '../../utils/billingHelpers';
import { CommentSubject, fetchComments } from './commentSubject';

interface SubjectComments {
  comments: CommentReturn[];
  loading: boolean;
  error: string | null;
  clearError: () => void;
  setError: (message: string) => void;
  reload: () => void;
}

/**
 * A violation's or work order's thread, as the server chose to show it to
 * this caller.
 *
 * There is no visibility parameter: `visibility_scope_for` decides by role
 * on the server, so a Manager gets both threads in one list and a
 * BoardMember gets only the shared one. Partition what comes back; never
 * assume the internal comments are present.
 *
 * Pagination is deliberately not sent. It engages only when both `page`
 * and `per_page` are given, and neither is declared in the OpenAPI path,
 * so the generated method does not accept them — a thread wants the whole
 * list in order anyway.
 *
 * Writes refetch rather than patching local state: a comment the client
 * invented would have to guess `author_id`, `created_at` and the
 * visibility the server actually stored, and that last one is the fact
 * this whole feature turns on. For the same reason a new comment is not
 * an optimistic write (#561): it is created with an id only the server
 * knows, so sending it twice would post it twice.
 *
 * With the sync engine's copy (#561) the thread is read from it — no
 * request, and other people's replies appear as they are posted. After
 * this tab writes, the refetched list is shown until the engine's echo of
 * that write changes the copy, so a new comment shows at once rather than
 * a moment later.
 */
export const useComments = (subject: CommentSubject): SubjectComments => {
  // Primitives, not the object: a caller building `{ kind, id }` inline
  // would otherwise refetch on every render.
  const { kind, id } = subject;

  const [comments, setComments] = useState<CommentReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const synced = useSyncedComments({ kind, id });
  const haveSynced = synced !== null;
  const syncedRef = useRef(synced);
  syncedRef.current = synced;
  // The refetch after a write, and the copy as it stood when it landed.
  const [afterWrite, setAfterWrite] = useState<{
    comments: CommentReturn[];
    copy: CommentReturn[] | null;
  } | null>(null);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);
  const clearError = useCallback(() => setErrorState(null), []);
  const setError = useCallback((message: string) => setErrorState(message), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // From the copy: nothing to fetch on the way in.
      if (haveSynced && reloadCount === 0) {
        setLoading(false);
        return;
      }
      try {
        if (!haveSynced) setLoading(true);
        const response = await fetchComments({ kind, id });
        if (cancelled) return;
        setComments(response.data.comments ?? []);
        if (haveSynced) {
          setAfterWrite({ comments: response.data.comments ?? [], copy: syncedRef.current });
        }
        setErrorState(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading comments:', err);
        setErrorState(
          billingErrorMessage(err, 'The comments could not be loaded.')
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [kind, id, reloadCount, haveSynced]);

  // A new subject starts from the copy again.
  useEffect(() => {
    setAfterWrite(null);
    setReloadCount(0);
  }, [kind, id]);

  const shown = !synced
    ? comments
    : afterWrite && afterWrite.copy === synced
      ? afterWrite.comments
      : synced;
  return { comments: shown, loading: haveSynced ? false : loading, error, clearError, setError, reload };
};
